import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  Shield,
  FileText,
  Users,
  Chrome,
  ArrowRight,
  RefreshCw,
  Check,
  Globe,
  Layers,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { useAuth } from '../hooks/useAuth';
import { CHROME_WEBSTORE_URL } from '../utils/constants';
import './LandingPage.css';

export function LandingPage() {
  usePageTitle(null);
  const { isAuthenticated } = useAuth();
  return (
    <div className="landing-container">
      {/* Navbar */}
      <header className="landing-header">
        <div className="landing-nav">
          <div className="landing-logo">
            <img src="/logo.png" alt="Lictor" className="landing-logo-icon" style={{ width: 32, height: 32 }} />
            <span>Lictor</span>
            <span style={{ fontSize: 'var(--text-xs)', background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-primary-400)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '1px 6px', borderRadius: '10px', marginLeft: '6px', fontWeight: 'var(--font-semibold)' }}>BETA</span>
          </div>

          <nav className="landing-nav-links">
            <a href="#funcionalidades" className="landing-nav-link">Funcionalidades</a>
            <a href="#precos" className="landing-nav-link">Planos</a>
            <a href="#seguranca" className="landing-nav-link">Segurança</a>
          </nav>

          <div className="landing-nav-ctas">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button variant="primary" size="sm">Acessar Painel</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">Entrar</Button>
                </Link>
                <Link to="/register">
                  <Button variant="primary" size="sm">Criar Conta (Versão Beta)</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content animate-fade-in-up">
          <div className="landing-badge">
            <Layers size={14} />
            <span>Versão Beta • Hub inteligente de acessos jurídicos</span>
          </div>

          <h1 className="landing-hero-title">
            Organize os acessos a tribunais e sistemas do escritório em <span>um único hub</span>
          </h1>

          <p className="landing-hero-subtitle">
            Dezenas de tribunais, sistemas de clientes e ferramentas de trabalho diferentes. O Lictor centraliza todos os acessos em um só lugar, com preenchimento seguro e organização inteligente.
          </p>

          <div className="landing-hero-ctas">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button variant="primary" size="lg" icon={<ArrowRight size={18} />}>
                  Acessar Painel
                </Button>
              </Link>
            ) : (
              <Link to="/register">
                <Button variant="primary" size="lg" icon={<ArrowRight size={18} />}>
                  Criar Conta
                </Button>
              </Link>
            )}
            <a href={CHROME_WEBSTORE_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="lg" icon={<Chrome size={18} />}>
                Instalar Extensão Chrome
              </Button>
            </a>
          </div>
        </div>

        {/* Large Video/Mockup Showcase */}
        <div className="landing-hero-media-wrapper animate-fade-in-up" style={{ marginTop: 'var(--space-12)' }}>
          <div className="landing-hero-media-video">
            <video
              src="/lictor_autofill_login.mp4"
              className="landing-hero-video"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
        </div>
      </section>

      {/* Key Features Grid */}
      <section id="funcionalidades" className="landing-section">
        <div className="landing-section-header">
          <span className="landing-section-subtitle">Funcionalidades</span>
          <h2 className="landing-section-title">Um hub centralizado para todos os seus sistemas</h2>
          <p className="landing-section-description">
            Esqueça planilhas expostas, bloqueios de contas de tribunais e mensagens de WhatsApp pedindo códigos de 2FA. Centralize tudo em um único lugar.
          </p>
        </div>

        <div className="landing-grid landing-grid--5">
          <div className="landing-card">
            <Globe className="landing-card-icon" />
            <h3 className="landing-card-title">1 Credencial = 1 Sistema</h3>
            <p className="landing-card-description">
              Cadastre cada sistema/URL separadamente. A extensão identifica automaticamente o tribunal e preenche os campos certos, sem conflitos.
            </p>
          </div>

          <div className="landing-card">
            <Chrome className="landing-card-icon" />
            <h3 className="landing-card-title">Login Automático</h3>
            <p className="landing-card-description">
              Nossa extensão de navegador detecta o tribunal e preenche CPF/OAB, Senha e TOTP automaticamente em segundos.
            </p>
          </div>

          <div className="landing-card">
            <RefreshCw className="landing-card-icon" />
            <h3 className="landing-card-title">2FA (TOTP) Integrado</h3>
            <p className="landing-card-description">
              O Lictor guarda o segredo do 2FA (cifrado) e gera o código na hora do login. A equipe entra sem precisar do celular do sócio.
            </p>
          </div>

          <div className="landing-card">
            <Users className="landing-card-icon" />
            <h3 className="landing-card-title">Controle de Acessos</h3>
            <p className="landing-card-description">
              Advogados e estagiários usam as credenciais pela extensão sem que a senha apareça na tela. Você escolhe quem usa o quê, por grupo.
            </p>
          </div>

          <div className="landing-card">
            <FileText className="landing-card-icon" />
            <h3 className="landing-card-title">Registro de atividades</h3>
            <p className="landing-card-description">
              Cada uso de credencial fica registrado: qual colaborador, qual tribunal, quando e de onde.
            </p>
          </div>
        </div>
      </section>

      {/* Split/Showcase Section */}
      <section id="como-funciona" className="landing-section" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
        <div className="landing-section-header">
          <span className="landing-section-subtitle">Fluxo Simplificado</span>
          <h2 className="landing-section-title">Como funciona no dia a dia?</h2>
        </div>

        <div className="landing-showcase">
          <div className="landing-showcase-row">
            <div className="landing-showcase-text">
              <h3>1. Cadastre uma Credencial por Sistema</h3>
              <p>
                Para cada sistema que seu escritório acessa (eProc, PJe, eSAJ, Projudi...), crie uma credencial com a URL correspondente. Isso permite que a extensão identifique automaticamente qual credencial usar em cada tribunal — funcionando como um hub de acesso rápido.
              </p>
            </div>
            <div className="landing-showcase-media landing-showcase-media--img">
              <img
                src="/features_credentials.png"
                alt="Painel de Cadastro de Credenciais"
                className="landing-showcase-img"
              />
            </div>
          </div>

          <div className="landing-showcase-row">
            <div className="landing-showcase-text">
              <h3>2. Distribuição por Grupos</h3>
              <p>
                Organize acessos em grupos específicos. Adicione, por exemplo, a "Equipe Cível" ao grupo correspondente, garantindo que membros comuns apenas usem as contas necessárias, sem revelação de senhas.
              </p>
            </div>
            <div className="landing-showcase-media landing-showcase-media--img">
              <img
                src="/features_groups.png"
                alt="Gerenciamento de Grupos e Permissões"
                className="landing-showcase-img"
              />
            </div>
          </div>

          <div className="landing-showcase-row">
            <div className="landing-showcase-text">
              <h3>3. Registro de atividades</h3>
              <p>
                Cada uso de credencial gera um registro com a pessoa, o tribunal, a data e o local de acesso. Administradores consultam e filtram esse histórico no painel.
              </p>
            </div>
            <div className="landing-showcase-media landing-showcase-media--img">
              <img
                src="/features_audit_log.png"
                alt="Registro de atividades do escritório"
                className="landing-showcase-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="landing-section" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
        <div className="landing-section-header">
          <span className="landing-section-subtitle">Planos</span>
          <h2 className="landing-section-title">O plano perfeito para a sua equipe</h2>
          <p className="landing-section-description">
            Planos flexíveis para atender à demanda de seu escritório.
          </p>
        </div>

        <div className="landing-pricing-grid">
          {/* Plan 1: Plan Único */}
          <div className="landing-pricing-card featured">
            <div className="landing-pricing-badge">Mais Popular</div>

            <div className="landing-pricing-header">
              <h3 className="landing-pricing-plan-title">Plano Único</h3>
              <div className="landing-pricing-price">
                R$ 299<span>/mês</span>
              </div>
              <div className="landing-pricing-price-sub">
                Inclui até 10 usuários ativos
              </div>
              <div className="landing-pricing-price-extra">
                + R$ 10/mês por usuário adicional
              </div>
            </div>

            <ul className="landing-pricing-list">
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Credenciais ilimitadas</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Preenchimento automático pela extensão</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>2FA (TOTP) automático e centralizado</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Controle de acesso por grupos e papéis</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Registro de atividades com filtros</span>
              </li>
            </ul>

            <Link to={isAuthenticated ? "/dashboard" : "/register"} style={{ marginTop: 'auto' }}>
              <Button variant="primary" fullWidth>{isAuthenticated ? "Acessar Painel" : "Começar Agora"}</Button>
            </Link>
          </div>

          {/* Plan 2: Sob Medida */}
          <div className="landing-pricing-card">
            <div className="landing-pricing-header">
              <h3 className="landing-pricing-plan-title">Sob Medida</h3>
              <div className="landing-pricing-price">
                Customizado
              </div>
              <div className="landing-pricing-price-sub" style={{ color: 'var(--color-text-secondary)' }}>
                Para grandes escritórios ou departamentos jurídicos
              </div>
            </div>

            <ul className="landing-pricing-list">
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Mais de 100 colaboradores ativos</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Suporte premium priorizado por telefone</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Customizações de layout / White Label</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Consultoria especializada de migração de senhas</span>
              </li>
              <li className="landing-pricing-item">
                <Check size={16} />
                <span>Acordo de Nível de Serviço (SLA) dedicado</span>
              </li>
            </ul>

            <a href="mailto:lictor.suporte@gmail.com" style={{ marginTop: 'auto' }}>
              <Button variant="outline" fullWidth>Falar com Vendas</Button>
            </a>
          </div>
        </div>
      </section>

      <section id="seguranca" className="landing-cta-section">
        <div className="landing-cta-content">
          <Shield size={48} style={{ margin: '0 auto var(--space-4)', color: 'var(--color-primary-400)' }} />
          <h2 className="landing-cta-title">Como protegemos suas senhas</h2>
          <ul className="landing-security-list">
            <li><strong>Cifra por credencial.</strong> Login, senha e segredo do 2FA são cifrados com AES-256, cada credencial com a sua própria chave.</li>
            <li><strong>Chave fora do banco.</strong> Essas chaves são protegidas pelo AWS KMS, o serviço de chaves da Amazon. Uma cópia do banco de dados, sozinha, não permite ler nenhuma senha.</li>
            <li><strong>Decifrada só no uso.</strong> A senha só é aberta no momento em que alguém com permissão usa a credencial. Membros usam pela extensão sem que a senha apareça na tela.</li>
            <li><strong>Tudo registrado.</strong> Cada uso, visualização e alteração fica no registro de atividades do escritório.</li>
          </ul>
        </div>
      </section>

      {/* CTA Final */}
      <section className="landing-section" style={{ textAlign: 'center', padding: 'var(--space-20) var(--space-6)' }}>
        <h2 className="landing-section-title" style={{ fontSize: 'var(--text-3xl)' }}>
          Pronto para organizar os acessos do seu escritório?
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-8)' }}>
          Centralize dezenas de sistemas jurídicos em um único hub. Acesso fácil, organizado e seguro — a agilidade que a advocacia moderna exige.
        </p>
        <Link to={isAuthenticated ? "/dashboard" : "/register"}>
          <Button variant="primary" size="lg">
            {isAuthenticated ? "Acessar Painel" : "Criar Minha Conta"}
          </Button>
        </Link>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-grid">
          <div className="landing-footer-col">
            <h4>Produto</h4>
            <div className="landing-footer-links">
              <a href="#funcionalidades" className="landing-footer-link">Funcionalidades</a>
              <a href="#como-funciona" className="landing-footer-link">Como funciona</a>
              <a href="#precos" className="landing-footer-link">Planos</a>
              <a href={CHROME_WEBSTORE_URL} target="_blank" rel="noopener noreferrer" className="landing-footer-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                Extensão Chrome <ExternalLink size={12} />
              </a>
            </div>
          </div>
          <div className="landing-footer-col">
            <h4>Plataforma</h4>
            <div className="landing-footer-links">
              {isAuthenticated ? (
                <Link to="/dashboard" className="landing-footer-link">Acessar painel</Link>
              ) : (
                <>
                  <Link to="/login" className="landing-footer-link">Entrar no painel</Link>
                  <Link to="/register" className="landing-footer-link">Criar conta</Link>
                </>
              )}
            </div>
          </div>
          <div className="landing-footer-col">
            <h4>Jurídico</h4>
            <div className="landing-footer-links">
              <Link to="/terms" className="landing-footer-link">Termos de Uso</Link>
              <Link to="/privacy" className="landing-footer-link">Privacidade</Link>
            </div>
          </div>
          <div className="landing-footer-col">
            <h4>Suporte</h4>
            <div className="landing-footer-links">
              <a href="mailto:lictor.suporte@gmail.com" className="landing-footer-link">Contato</a>
              <Link to="/faq" className="landing-footer-link">FAQ</Link>
            </div>
          </div>
        </div>

        <div className="landing-footer-bottom" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', width: '100%' }}>
          <div className="landing-copyright">
            &copy; {new Date().getFullYear()} Lictor. Todos os direitos reservados.
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
            Contato: <a href="mailto:lictor.suporte@gmail.com" style={{ color: 'var(--color-primary-400)', textDecoration: 'none' }}>lictor.suporte@gmail.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
